import { useState, type FormEvent } from 'react';

import eyeOff from '../../../assets/eye-off.svg';
import eyeOn from '../../../assets/eye-on.svg';
import { Input } from '../../ui/Input/Input';
import { Button } from '../../ui/Button/Button';
import { PasswordValidation } from '../../validations/PasswordValidation';
import { ErrorMessage } from '../../ui/ErrorMessage/ErrorMessage';
import { PrimaryButton } from '../../ui/Button/Buttons';
import { authService } from '../../../services/AuthService';
import { showToast } from '../../../store/slices/toastSlice';
import { useAppDispatch } from '../../../hooks/redux';

type ResetPasswordFormProps = {
  setIsSent: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  verificationToken: string;
};

export function ResetPasswordForm({
  setIsSent,
  email,
  verificationToken
}: ResetPasswordFormProps) {
  const dispatch = useAppDispatch();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const match = password === passwordConfirm;
  const disableResetButton = !match || loading || verificationToken === '';

  const handleResetPassword = async () => {
    if (!password || !passwordConfirm) {
      dispatch(
        showToast({
          message: 'Please fill in all password fields.',
          type: 'error'
        })
      );
      return;
    }

    if (verificationToken === '') {
      dispatch(
        showToast({
          message: 'Invalid reset data. Please request a new password reset.',
          type: 'error'
        })
      );
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword({
        email,
        token: verificationToken,
        newPassword: password,
        confirmPassword: passwordConfirm
      });
      setIsSent(true);
    } catch (error: any) {
      dispatch(
        showToast({
          message:
            error?.message || 'Failed to reset password. Please try again.',
          type: 'error'
        })
      );
      if (error) {
        console.error('Error reseting password:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleResetPassword();
  };

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-6 self-stretch">
        <Input
          label="Password"
          id="password"
          type={showPassword ? 'text' : 'password'}
          required={true}
          value={password}
          maxLength={72}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
          containerClassName=""
          labelClassName="label"
          inputClassName={`input-password ${
            !match && passwordConfirm ? 'input-error' : ''
          }`}
        >
          {' '}
          {password && (
            <Button
              type="button"
              onClick={() => {
                setShowPassword(!showPassword);
              }}
              buttonClassName="absolute right-3 top-1/2 -translate-y-1/2 p-0 bg-transparent border-none"
            >
              <img
                src={showPassword ? eyeOn : eyeOff}
                alt="password"
                className="w-4 h-4"
              />
            </Button>
          )}
        </Input>
        <div className="flex flex-col gap-2">
          <Input
            label="Confirm Password"
            id="confirmPassword"
            type={showPasswordConfirm ? 'text' : 'password'}
            required={true}
            value={passwordConfirm}
            maxLength={72}
            onChange={(e) => {
              setPasswordConfirm(e.target.value);
            }}
            containerClassName=""
            labelClassName="label"
            inputClassName={`input-password ${
              !match && passwordConfirm ? 'input-error' : ''
            }`}
          >
            {' '}
            {passwordConfirm && (
              <Button
                type="button"
                onClick={() => {
                  setShowPasswordConfirm(!showPasswordConfirm);
                }}
                buttonClassName="absolute right-3 top-1/2 -translate-y-1/2 p-0 bg-transparent border-none"
              >
                <img
                  src={showPasswordConfirm ? eyeOn : eyeOff}
                  alt="password"
                  className="w-4 h-4"
                />
              </Button>
            )}
          </Input>

          {!match && passwordConfirm && (
            <ErrorMessage message={'Passwords do not match.'} />
          )}
        </div>
        <PasswordValidation password={password} />
        <PrimaryButton
          type="submit"
          className="w-full"
          disabled={disableResetButton}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </PrimaryButton>
      </div>
    </form>
  );
}
